import useTranslator from "../../hooks/useTranslator";
import Form from "react-bootstrap/Form";
import Cancelimg from "../../assets/img/cancel-01.svg";
import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../../services/authWrapper";
import { toast } from "../../helper/common";
import LoadingSpinner from "../../components/LoadingSpinner";
import AuthStepHeader from "../../components/Auth/AuthStepHeader";
import { Button, CodeInput } from "../../components/design";
import "../../components/Auth/AuthForm.css";

type OtpVerifyHandler = (event: any) => void;

interface EnterOtpModalProps {
  otp: string;
  setOtp: (otp: string) => void;
  isGuarantor?: boolean;
  onClose: () => void;
  handleSubmitEditedUser?: (verified: boolean) => void;
  showResend: boolean;
  showTimer: number;
  sendOtpFun: () => void;
  verifyOTPHandler?: OtpVerifyHandler;
  verifyOtpLoading: boolean;
  email: string;
  /** show as a step inside the login page instead of a popup */
  inline?: boolean;
  /** The containing shared Modal supplies the title, instruction and email. */
  hideHeader?: boolean;
  isDummySignup?: boolean;
  setStep?: () => {};
}

const EnterOtpModal = ({
  otp,
  setOtp,
  isGuarantor,
  onClose,
  handleSubmitEditedUser,
  showResend,
  showTimer,
  sendOtpFun,
  verifyOTPHandler,
  verifyOtpLoading,
  email,
  inline = false,
  hideHeader = false,
  isDummySignup,
  setStep,
}: EnterOtpModalProps) => {
  const { _t } = useTranslator();
  const [otpSubmitLoading, setOtpSubmitLoading] = useState(false);
  const submittedOtpRef = useRef("");
  const otpWrapperRef = useRef<HTMLDivElement | null>(null);
  const wasLoadingRef = useRef(false);

  const focusLastInput = useCallback(() => {
    if (otpWrapperRef.current) {
      const inputs = otpWrapperRef.current.querySelectorAll("input");
      if (inputs.length > 0) inputs[inputs.length - 1].focus();
    }
  }, []);

  useEffect(() => {
    const isLoading = otpSubmitLoading || verifyOtpLoading;
    if (wasLoadingRef.current && !isLoading) {
      focusLastInput();
    }
    wasLoadingRef.current = isLoading;
  }, [otpSubmitLoading, verifyOtpLoading, focusLastInput]);

  const verifyOTP = useCallback(async () => {
    const userid = localStorage.getItem("saveduserid");
    const usertype = localStorage.getItem("usertype");
    setOtpSubmitLoading(true);
    await axiosInstance
      .put(
        `${process.env.REACT_APP_SERVER_URL}/users/otp-verify/${userid}`,
        { type: usertype, otp: otp },
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      )
      .then((res) => {
        if (res?.data?.status === "verified") {
          handleSubmitEditedUser?.(true);
        }
      })
      .catch((err) => {
        const serverResponse = err;
        toast.error(serverResponse.response.data.msg);
      })
      .finally(() => {
        setOtpSubmitLoading(false);
      });
  }, [handleSubmitEditedUser, otp]);

  const handleVerifyOTP = useCallback(
    (event: any) => {
      (verifyOTPHandler ? verifyOTPHandler : verifyOTP)(event);
    },
    [verifyOTPHandler, verifyOTP]
  );

  useEffect(() => {
    if ((otp ?? "").length < 6) {
      submittedOtpRef.current = "";
      return;
    }

    if (
      (otp ?? "").length === 6 &&
      !(otpSubmitLoading || verifyOtpLoading) &&
      submittedOtpRef.current !== otp
    ) {
      submittedOtpRef.current = otp;
      handleVerifyOTP({ preventDefault: () => {} });
    }
  }, [handleVerifyOTP, otp, otpSubmitLoading, verifyOtpLoading]);

  const isBusy = otpSubmitLoading || verifyOtpLoading;

  if (inline) {
    return (
      <div className="auth-step">
        {!hideHeader && (
          <AuthStepHeader
            onBack={onClose}
            title={_t("checkYourEmailForOtp")}
            subtitle={_t("anOtpHasBeenSentToYourEmailPleaseCheck")}
          >
            <p className="auth-step-email">{email}</p>
          </AuthStepHeader>
        )}
        <div
          ref={otpWrapperRef}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (otp ?? "").length === 6 && !isBusy) {
              handleVerifyOTP(e);
            }
          }}
        >
          <CodeInput
            value={otp}
            onChange={setOtp}
            autoFocus
            disabled={isBusy}
            aria-label={_t("checkYourEmailForOtp")}
          />
        </div>
        <div className="auth-otp-status" aria-live="polite">
          {isBusy && <span className="auth-spinner" aria-hidden="true" />}
        </div>
        {showResend ? (
          <p className="auth-footer">
            {_t("cannotFindOtpEmailPleaseCheckSpamAsWellOrTryAgain")}{" "}
            <Button
              variant="link"
              disabled={isBusy}
              onClick={() => sendOtpFun()}
            >
              {_t("resend")}
            </Button>
          </p>
        ) : (
          <p className="auth-footer">
            {_t("resentOtpIn")} <strong>{showTimer}s</strong>
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className={`approvedModal popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 wrapper-modal modal-fixed-screen ${
        isGuarantor ? "guarantorBg" : ""
      }`}
      style={{ zIndex: 99 }}
    >
      <div
        className="popup-content2 position-relative popup-content bg-white required-doc-popup enterOtpPopup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex custom-mb-3 align-items-center justify-content-between">
          <h2 className="headerOtp">{_t("checkYourEmailForOtp")}</h2>
          <img
            src={Cancelimg}
            alt="icon"
            className="canceicon"
            onClick={onClose}
          />
        </div>
        <p>{_t("anOtpHasBeenSentToYourEmailPleaseCheck")}</p>
        <p className="fw-bold">{email}</p>
        <Form
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              (otp ?? "").length === 6 &&
              !(otpSubmitLoading || verifyOtpLoading)
            ) {
              handleVerifyOTP(e);
            }
          }}
          className="d-flex justify-content-center my-3"
        >
          <div ref={otpWrapperRef}>
            <CodeInput
              value={otp}
              onChange={setOtp}
              autoFocus
              disabled={otpSubmitLoading || verifyOtpLoading}
              aria-label={_t("checkYourEmailForOtp")}
            />
          </div>
        </Form>
        <div className="d-flex align-items-center justify-content-center mb-2">
          {otpSubmitLoading || verifyOtpLoading ? (
            <LoadingSpinner
              size={"1.111vw" as any}
              color="#253837"
              borderWidth={1}
              className="docs-loading-spinner"
            />
          ) : null}
        </div>
        {showResend ? (
          <>
            <p className="notFindText">
              {_t("cannotFindOtpEmailPleaseCheckSpamAsWellOrTryAgain")}
            </p>
            <p
              onClick={() => sendOtpFun()}
              className="hover-underline resendButton cursor-pointer"
              style={{
                pointerEvents:
                  otpSubmitLoading || verifyOtpLoading ? "none" : "auto",
                opacity: otpSubmitLoading || verifyOtpLoading ? 0.5 : 1,
              }}
            >
              {_t("resend")}
            </p>
          </>
        ) : (
          <p className="resendOtpText custom-gap-2 align-items-center justify-content-center">
            {_t("resentOtpIn")}
            <span>{showTimer}s</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default EnterOtpModal;
